import { useEffect, useMemo, useState } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Drawer, { FormField, FormTextarea, FormSelect, getSaveLabel } from "@/components/Drawer";
import BranchSelect from "@/components/BranchSelect";
import PaymentLinesEditor from "@/components/PaymentLinesEditor";
import { fmtMoney } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const PARTY_TYPES = [
  { value: "customer", label: "Customer" },
  { value: "financier", label: "Financier / Bank" },
  { value: "evaluator", label: "Evaluator / Agent" },
  { value: "vendor", label: "Vendor / Supplier" },
  { value: "staff", label: "Staff / Internal" },
  { value: "other", label: "Other" },
];

const VOUCHER_METHODS = ["cash", "upi", "card", "online", "bank", "cheque"];

const EMPTY = {
  kind: "receipt",
  branch_id: "",
  date: new Date().toISOString().slice(0, 10),
  party_type: "other",
  party_id: "",
  party_name: "",
  lines: [], // [{ _id, method, amount, reference }]
  narration: "",
  related_sale_id: "",
  related_booking_id: "",
};

export default function VoucherFormDrawer({
  open,
  defaultKind = "receipt",
  preset = null,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({ ...EMPTY, kind: defaultKind });
  const [parties, setParties] = useState({ customer: [], financier: [], evaluator: [] });
  const [saving, setSaving] = useState(false);
  // Files staged at create time — uploaded as evidence right after the
  // voucher is saved. Lets accounts approvers see supporting documents
  // without a second round-trip.
  const [evidenceFiles, setEvidenceFiles] = useState([]);

  const [showCustomName, setShowCustomName] = useState(false);

  // Backdating policy — branch operators must enter vouchers on the same
  // day they happen. Only admins can pick a historical date (e.g. fixing a
  // mis-entered voucher from yesterday). This pins the date picker to today
  // for non-admins via the input's `min`/`max` attributes.
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = new Date().toISOString().slice(0, 10);
  // When a non-admin opens the drawer with a stale preset.date (e.g. yesterday),
  // snap it back to today so they don't accidentally submit a backdated voucher.
  useEffect(() => {
    if (open && !isAdmin && form.date !== today) {
      setForm((f) => ({ ...f, date: today }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAdmin]);

  useEffect(() => {
    if (!open) return;
    setForm({ ...EMPTY, kind: defaultKind, ...(preset || {}) });
    setShowCustomName(false);
    setEvidenceFiles([]);
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/vouchers/meta/party-options");
        if (!cancelled) setParties(r.data || {});
      } catch {
        /* non-fatal */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, defaultKind, preset]);

  const partySuggestions = parties[form.party_type] || [];

  const total = useMemo(
    () => (form.lines || []).reduce((s, ln) => s + Number(ln.amount || 0), 0),
    [form.lines],
  );

  const confirm = useConfirm();
  const submit = async (e) => {
    e.preventDefault();
    if (!(await confirm({ title: "Save voucher?", body: "This voucher will be recorded in the cashbook. Confirm before saving." }))) return;
    if (!form.party_name.trim()) {
      toast.error("Party name is required.");
      return;
    }
    if (!form.branch_id) {
      toast.error("Pick a branch for the voucher.");
      return;
    }
    if (!form.lines.length) {
      toast.error("Add at least one payment line.");
      return;
    }
    if (total <= 0) {
      toast.error("Total must be greater than zero.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        kind: form.kind,
        branch_id: form.branch_id,
        date: form.date,
        party_type: form.party_type,
        party_id: form.party_id || null,
        party_name: form.party_name,
        narration: form.narration,
        related_sale_id: form.related_sale_id || null,
        related_booking_id: form.related_booking_id || null,
        lines: form.lines.map((ln) => ({
          method: ln.method,
          amount: Number(ln.amount),
          reference: (ln.reference || "").trim(),
        })),
      };
      const { data: saved } = await api.post("/vouchers", payload);
      // Upload any staged evidence right after the voucher is created.
      // We do this sequentially to surface a clear toast per failure
      // rather than swallowing them in a Promise.all.
      for (const f of evidenceFiles) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          await api.post(`/vouchers/${saved.id}/evidence`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (err) {
          toast.error(`Evidence "${f.name}" failed: ${formatApiError(err)}`);
        }
      }
      toast.success(
        form.kind === "receipt" ? "Receipt voucher saved" : "Payment voucher saved",
      );
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="Vouchers"
      title={form.kind === "receipt" ? "New Receipt Voucher" : "New Payment Voucher"}
      testIdClose="close-voucher-form"
    >
      <form onSubmit={submit} data-testid="voucher-form" className="space-y-4">
        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
            Voucher Kind
          </label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { v: "receipt", l: "Receipt", n: "Money IN" },
              { v: "payment", l: "Payment", n: "Money OUT" },
            ].map((o) => {
              const active = form.kind === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, kind: o.v }))}
                  data-testid={`voucher-kind-${o.v}`}
                  className={`px-3 py-2 border text-sm text-left transition-colors ${
                    active
                      ? o.v === "receipt"
                        ? "border-[#00B341] bg-[#00B341] text-white"
                        : "border-destructive bg-destructive text-white"
                      : "border-canvas-line text-ink hover:border-ink"
                  }`}
                >
                  <div className="font-semibold">{o.l}</div>
                  <div
                    className={`font-mono text-[10px] uppercase tracking-wider ${
                      active ? "text-white/80" : "text-ink-subtle"
                    }`}
                  >
                    {o.n}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label={isAdmin ? "Date" : "Date (today)"}
            type="date"
            value={form.date}
            onChange={(v) => setForm((f) => ({ ...f, date: v }))}
            testId="voucher-date"
            required
            min={isAdmin ? undefined : today}
            max={isAdmin ? undefined : today}
          />
          <BranchSelect
            value={form.branch_id}
            onChange={(v) => setForm((f) => ({ ...f, branch_id: v }))}
            testId="voucher-branch"
            required
          />
        </div>

        <FormSelect
          label={form.kind === "receipt" ? "Received From (Type)" : "Paid To (Type)"}
          value={form.party_type}
          onChange={(v) => {
            setForm((f) => ({ ...f, party_type: v, party_id: "", party_name: "" }));
            setShowCustomName(false);
          }}
          options={PARTY_TYPES}
          testId="voucher-party-type"
        />

        <div>
          <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2">
            {form.kind === "receipt" ? "Received From" : "Paid To"}
          </label>
          <select
            data-testid="voucher-party-id"
            value={showCustomName ? "__custom__" : form.party_id || (form.party_name ? `__name__${form.party_name}` : "")}
            onChange={(e) => {
              const val = e.target.value;
              if (val === "__custom__") {
                setShowCustomName(true);
                setForm((f) => ({ ...f, party_id: "", party_name: "" }));
                return;
              }
              setShowCustomName(false);
              if (val.startsWith("__name__")) {
                // History-suggested name without an id
                setForm((f) => ({ ...f, party_id: "", party_name: val.slice("__name__".length) }));
                return;
              }
              const hit = partySuggestions.find((p) => p.id === val);
              setForm((f) => ({
                ...f,
                party_id: val,
                party_name: hit ? hit.name : "",
              }));
            }}
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          >
            <option value="" disabled>
              — Select {form.kind === "receipt" ? "payer" : "payee"} —
            </option>
            {partySuggestions.map((p) =>
              p.id ? (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ) : (
                <option key={`name-${p.name}`} value={`__name__${p.name}`}>
                  {p.name}
                </option>
              ),
            )}
            <option value="__custom__">+ Add new…</option>
          </select>
          {showCustomName && (
            <input
              data-testid="voucher-party-name"
              value={form.party_name}
              onChange={(e) => setForm((f) => ({ ...f, party_name: e.target.value }))}
              placeholder="Type a custom party name"
              className="w-full mt-2 px-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
            />
          )}
        </div>

        <PaymentLinesEditor
          value={form.lines}
          onChange={(lines) => setForm((f) => ({ ...f, lines }))}
          methods={VOUCHER_METHODS}
          label="Tender Lines"
        />

        <FormTextarea
          label="Narration"
          value={form.narration}
          onChange={(v) => setForm((f) => ({ ...f, narration: v }))}
          testId="voucher-narration"
        />

        <div
          className="px-3 py-2 border border-canvas-line bg-canvas-panel font-mono text-[11px] text-ink flex justify-between"
          data-testid="voucher-summary-strip"
        >
          <span className="text-ink-muted uppercase tracking-wider">
            {form.kind === "receipt" ? "Will receive" : "Will pay"}
          </span>
          <span className="font-bold" data-testid="voucher-total">
            {total > 0 ? fmtMoney(total) : "—"}
          </span>
        </div>

        {/* Evidence — attached up-front so accounts approvers see it before approving */}
        <div className="border border-canvas-line p-3 space-y-2">
          <div className="flex items-center justify-between">
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted">
              Evidence (images / PDF — optional)
            </label>
            <label
              data-testid="voucher-evidence-picker"
              className="px-3 py-1.5 border border-ink text-ink text-[11px] font-semibold cursor-pointer hover:bg-ink hover:text-white transition-colors"
            >
              + Attach file
              <input
                type="file"
                accept="image/*,application/pdf"
                multiple
                className="hidden"
                onChange={(e) => {
                  const picked = Array.from(e.target.files || []);
                  if (picked.length > 0) setEvidenceFiles((prev) => [...prev, ...picked]);
                  e.target.value = "";
                }}
              />
            </label>
          </div>
          {evidenceFiles.length === 0 ? (
            <div className="font-mono text-[10px] text-ink-subtle">No file attached. You can also add evidence later from the vouchers list.</div>
          ) : (
            <ul className="space-y-1">
              {evidenceFiles.map((f, i) => (
                <li
                  key={i}
                  className="flex items-center justify-between gap-2 px-2 py-1.5 bg-canvas-panel border border-canvas-line text-xs"
                  data-testid={`voucher-staged-evidence-${i}`}
                >
                  <span className="truncate flex-1">{f.name}</span>
                  <span className="font-mono text-[10px] text-ink-muted">{(f.size / 1024).toFixed(1)} KB</span>
                  <button
                    type="button"
                    onClick={() => setEvidenceFiles((prev) => prev.filter((_, idx) => idx !== i))}
                    className="px-2 text-destructive font-bold"
                    title="Remove"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="pt-4 flex items-center gap-2 justify-end border-t border-canvas-line -mx-6 px-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="confirm-voucher-button"
            className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors disabled:opacity-60"
          >
            {getSaveLabel(saving, false, "Saving…", "", "Save voucher")}
          </button>
        </div>
      </form>
    </Drawer>
  );
}
